import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";

import { AdminLocaleRoute } from "@/components/core/admin-locale-route";
import { LocaleRoute } from "@/components/core/locale-route";
import { NonLocaleRoute } from "@/components/core/non-locale-route";
import { RootRedirect } from "@/components/core/root-redirect";
import { LOCALES } from "@/i18n/types";
import { HomePage } from "@/pages/home-page";

const AboutPage = lazy(() =>
  import("@/pages/about-page").then((m) => ({ default: m.AboutPage })),
);
const AdminInquiriesPage = lazy(() =>
  import("@/pages/admin-inquiries-page").then((m) => ({
    default: m.AdminInquiriesPage,
  })),
);
const AdminLoginPage = lazy(() =>
  import("@/pages/admin-login-page").then((m) => ({
    default: m.AdminLoginPage,
  })),
);
const InquiryPage = lazy(() =>
  import("@/pages/inquiry-page").then((m) => ({ default: m.InquiryPage })),
);
const LegalPage = lazy(() =>
  import("@/pages/legal-page").then((m) => ({ default: m.LegalPage })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/not-found-page").then((m) => ({ default: m.NotFoundPage })),
);
const ProcessPage = lazy(() =>
  import("@/pages/process-page").then((m) => ({ default: m.ProcessPage })),
);
const ServicesPage = lazy(() =>
  import("@/pages/services-page").then((m) => ({ default: m.ServicesPage })),
);

export interface PrerenderRoute {
  locale: string;
  path: string;
}

export const PUBLIC_PAGES = [
  { path: "", title: "home" },
  { path: "about", title: "about" },
  { path: "services", title: "services" },
  { path: "process", title: "process" },
  { path: "inquiry", title: "inquiry" },
  { path: "legal", title: "legal" },
] as const;

export function getPrerenderRoutes(): PrerenderRoute[] {
  const routes: PrerenderRoute[] = [];
  for (const locale of LOCALES) {
    for (const { path } of PUBLIC_PAGES) {
      routes.push({
        locale,
        path: path ? `/${locale}/${path}` : `/${locale}`,
      });
    }
  }
  return routes;
}

function SuspenseRoute({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route element={<RootRedirect />} path="/" />

      <Route element={<AdminLocaleRoute />} path=":locale/admin">
        <Route
          element={
            <SuspenseRoute>
              <AdminLoginPage />
            </SuspenseRoute>
          }
          index
        />
        <Route
          element={
            <SuspenseRoute>
              <AdminInquiriesPage />
            </SuspenseRoute>
          }
          path="inquiries"
        />
      </Route>

      <Route element={<LocaleRoute />} path=":locale">
        <Route element={<HomePage />} index />
        <Route
          element={
            <SuspenseRoute>
              <AboutPage />
            </SuspenseRoute>
          }
          path="about"
        />
        <Route
          element={
            <SuspenseRoute>
              <ServicesPage />
            </SuspenseRoute>
          }
          path="services"
        />
        <Route
          element={
            <SuspenseRoute>
              <ProcessPage />
            </SuspenseRoute>
          }
          path="process"
        />
        <Route
          element={
            <SuspenseRoute>
              <InquiryPage />
            </SuspenseRoute>
          }
          path="inquiry"
        />
        <Route
          element={
            <SuspenseRoute>
              <LegalPage />
            </SuspenseRoute>
          }
          path="legal"
        />
        <Route
          element={
            <SuspenseRoute>
              <NotFoundPage />
            </SuspenseRoute>
          }
          path="*"
        />
      </Route>

      <Route
        element={
          <NonLocaleRoute>
            <SuspenseRoute>
              <NotFoundPage />
            </SuspenseRoute>
          </NonLocaleRoute>
        }
        path="*"
      />
    </Routes>
  );
}
